import {
  AppService,
  OSDService,
  ManifestService,
  EventsService,
} from "../services/orphan.service.js";
import PrintService from "../services/print.service.js";

interface PrintResponse {
  // Add properties that match the API's response structure
}

interface PrintError {
  isError: boolean;
  status: number;
  title: string;
  // Add properties that match the API's error structure
}

/**
 * Shared failure path for the manifest commands whose callers inspect the response.
 *
 * Those actions used to swallow the error here and return undefined, which *resolved* the promise.
 * The caller's .then then ran with res === undefined and crashed on res.result, so one rejected
 * command produced two toasts on the dock: the real reason from toast/error, and a raw
 * "TypeError: Cannot read properties of undefined (reading 'result')" from the caller's .catch.
 * That is what the LAX crew was looking at on 2026-09-17 while Close Inbound refused a manifest.
 *
 * Toast once here, then reject with the error marked as already reported so the caller can skip a
 * second toast. Mirrors the CommandRejected convention used by the load action below. Status is
 * carried across so isUnauthorized() still recognises a 401 at the call site.
 */
const rejectHandled = (dispatch, error) => {
  let title = error?.title || "Error";

  if (error?.response) {
    try {
      title = JSON.parse(error.response).title || title;
    } catch {
      // A non-JSON error body used to throw here, inside the catch, and surface as an unhandled
      // rejection instead of the message the server actually sent.
    }
  }

  if (error?.isError && error.status == 401) {
    dispatch("auth/logout", {}, { root: true });
  } else {
    dispatch("toast/error", title, { root: true });
  }

  const handled: Error & { status?: number; alreadyToasted?: boolean } = new Error(title);
  handled.name = "RequestFailed";
  handled.status = error?.status;
  handled.alreadyToasted = true;
  return Promise.reject(handled);
};

const initialState = {
  _activeManifest: null,
  _manifests: [],
  _statuses: [],
  _totalRecords: 0,
  _page: 1,
  _manifestTypes: [
    { name: "Pickup", code: "pickup" },
    { name: "Linehaul", code: "linehaul" },
    { name: "Delivery", code: "delivery" },
  ],
};

const createStore = () => {
  return {
    namespaced: true,
    state: initialState,
    getters: {
      activeManifest: (state) => state._activeManifest,
      manifests: (state) => state._manifests,
      statuses: (state) => state._statuses,
      totalRecords: (state) => state._totalRecords,
      page: (state) => state._page,
      size: (state) => state._manifests.length,
      client: (state, getters, rootState) => {
        return ManifestService.getInstance(rootState.auth.user.token);
      },
      eventsClient: (state, getters, rootState) => {
        return EventsService.getInstance(rootState.auth.user.token);
      },
      OSDClient: (state, getters, rootState) => {
        return OSDService.getInstance(rootState.auth.user.token);
      },
      appClient: (state, getters, rootState) => {
        return AppService.getInstance(rootState.auth.user.token);
      },
      printClient: (state, getters, rootState) => {
        return new PrintService(rootState.auth.user.token);
      },
      manifestTypes: (state) => state._manifestTypes,
    },
    actions: {
      getManifest: ({ commit, getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .manifestGet(params.manifestId)
          .then((data) => {
            console.log("SET_ACTIVE_MANIFEST", data);
            commit("SET_ACTIVE_MANIFEST", data.result);
          })
          .catch((error) => {
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
            throw error;
          });
      },

      getManifestAtVersion: ({ getters, dispatch }, params) => {
        const client = getters.client; // Use ManifestsClient, not EventsClient
        return new Promise((resolve, reject) => {
          // Validate version parameter
          if (params.version === undefined || params.version === null || params.version === 0) {
            reject(new Error("Version parameter must be a valid number greater than 0"));
            return;
          }
          client
            .manifestGetAtVersion(params.manifestId, params.version)
            .then((data) => {
              console.log(data);
              resolve(data.result);
            })
            .catch((error) => {
              console.log("ERROR!", error);
              if (error.isError && error.status == 401) {
                dispatch("auth/logout", {}, { root: true });
              } else {
                dispatch("toast/error", error.title, { root: true });
              }
              reject(error);
            });
        });
      },
      manifestEvents: ({ getters, dispatch }, manifestId) => {
        const client = getters.eventsClient;
        return new Promise((resolve, reject) => {
          client
            .manifestEvents(manifestId)
            .then((data) => {
              console.log(data);
              resolve(data.result);
            })
            .catch((error) => {
              console.log("ERROR!", error);
              if (error.isError && error.status == 401) {
                dispatch("auth/logout", {}, { root: true });
              } else {
                dispatch("toast/error", error.title, { root: true });
              }
              reject(error);
            });
        });
      },
      manifestEventsRange: ({ getters, dispatch }, params) => {
        const client = getters.eventsClient;
        return new Promise((resolve, reject) => {
          // Validate version parameter
          if (params.version === undefined || params.version === null || params.version === 0) {
            reject(new Error("Version parameter must be a valid number greater than 0"));
            return;
          }
          client
            .manifestEventsRange(params.manifestId, params.version, params.from)
            .then((data) => {
              console.log(data);
              resolve(data.result);
            })
            .catch((error) => {
              console.log("ERROR!", error);
              if (error.isError && error.status == 401) {
                dispatch("auth/logout", {}, { root: true });
              } else {
                dispatch("toast/error", error.title, { root: true });
              }
              reject(error);
            });
        });
      },

      getStatuses: ({ commit, getters, dispatch }) => {
        const client = getters.client;
        return client
          .manifestStatusesGet()
          .then((data) => {
            console.log('SET_STATUSES', data);
            commit('SET_STATUSES', data.result);
            // Cache the result
            localStorage.setItem('manifestStatuses', JSON.stringify(data.result));
          })
          .catch((error) => {
            let title = error.title || 'Error';

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch('auth/logout', {}, { root: true });
            } else {
              dispatch('toast/error', title, { root: true });
            }
          });
      },

      printLabels: ({ getters, dispatch }, params: { proNumber: string }) => {
        const printClient = getters.printClient;
        return printClient
          .printManifestLabels(params.proNumber)
          .then((response: PrintResponse) => {
            console.log(response);
          })
          .catch((error: PrintError) => {
            console.log("ERROR!", error);
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
          });
      },
      updateDestinationTerminal: ({ getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .updateDestinationTerminal(params.manifestId, {
            terminal: params.terminal.code,
          })
          .then((response) => {
            console.log(response);
            return response;
          })
          .catch((error) => {
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
          });
      },
      updateOriginTerminal: ({ getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .updateOriginTerminal(params.manifestId, {
            terminal: params.terminal.code,
          })
          .then((response) => {
            console.log(response);
            return response;
          })
          .catch((error) => {
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            }
            throw error;
          });
      },
      arrive: ({ getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .arrive(params.manifestId, {
            terminal: params.terminal,
            arrivalDate: new Date().toISOString(),
            arrived: true,
          })
          .then((response) => {
            console.log(response);
            return response;
          })
          .catch((error) => {
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            }
            throw error;
          });
      },
      close: ({ getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .close(params.manifestId)
          .then((response) => {
            console.log(response);
            return response;
          })
          .catch((error) => rejectHandled(dispatch, error));
      },
      closeInbound: ({ getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .closeInbound(params.manifestId)
          .then((response) => {
            console.log(response);
            return response;
          })
          .catch((error) => rejectHandled(dispatch, error));
      },
      depart: ({ getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .depart(params.manifestId, {
            terminal: params.terminal,
            departureDate: new Date().toISOString(),
          })
          .then((response) => {
            console.log(response);
            return response;
          })
          .catch((error) => rejectHandled(dispatch, error));
      },
      undepart: ({ getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .undepart(params.manifestId, { manifestId: params.manifestId })
          .then((response) => {
            console.log(response);
            return response;
          })
          .catch((error) => {
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
          });
      },
      reopen: ({ getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .reopen(params.manifestId, { manifestId: params.manifestId })
          .then((response) => {
            console.log(response);
            return response;
          })
          .catch((error) => {
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
          });
      },
      release: ({ getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .release(params.manifestId, { door: params.door })
          .then((response) => {
            console.log(response);
            return response;
          })
          .catch((error) => {
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            }
            throw error;
          });
      },
      shortItems: ({ getters, dispatch }, params) => {
        const client = getters.OSDClient;
        return client
          .shortItems(
            params.proNumber,
            params.sku,
            params.quantity,
            params.notes
          )
          .then((response) => {
            console.log(response);
            return response;
          })
          .catch((error) => {
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
            return error;
          });
      },
      shortItem: ({ getters, dispatch }, params) => {
        const client = getters.OSDClient;
        return client
          .shortItem({
            proNumber: params.proNumber,
            barcode: params.barcode,
            notes: params.notes,
          })
          .then((response) => {
            console.log(response);
            return response;
          })
          .catch((error) => {
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
            return error;
          });
      },
      damageItem: ({ getters, dispatch }, params) => {
        const client = getters.OSDClient;
        return client
          .damage({
            proNumber: params.proNumber,
            barcode: params.barcode,
            notes: params.notes,
          })
          .then((response) => {
            console.log(response);
            return response;
          })
          .catch((error) => {
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
            return error;
          });
      },
      destroyItem: ({ getters, dispatch }, params) => {
        const client = getters.OSDClient;
        return client
          .destroy({
            proNumber: params.proNumber,
            barcode: params.barcode,
            notes: params.notes,
          })
          .then((response) => {
            console.log(response);
            return response;
          })
          .catch((error) => {
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
            return error;
          });
      },
      orphanItem: ({ getters, dispatch }, params) => {
        const client = getters.OSDClient;
        return client
          .orphan({
            proNumber: params.proNumber,
            barcode: params.barcode,
            notes: params.notes,
          })
          .then((response) => {
            console.log(response);
            return response;
          })
          .catch((error) => {
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
            return error;
          });
      },

      setDefaultLocationForPro: ({ getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .setDefaultLocationForPro(
            params.manifestId,
            params.proNumber,
            params.location
          )
          .then((response) => {
            console.log(response);
            return response;
          })
          .catch((error) => {
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
            return error;
          });
      },

      assignTrailer: ({ getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .assignTrailer(params.manifestId, {
            manifestId: params.manifestId,
            trailer: params.trailer,
          })
          .then((response) => {
            console.log(response);
            return response;
          })
          .catch((error) => {
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
          });
      },
      deactivateTrackingUnit: ({ getters, dispatch }, params) => {
        const client = getters.appClient;
        return client
          .deactivateTrackingUnit({
            barcode: params.barcode,
            proNumber: params.proNumber,
          })
          .then((response) => {
            console.log(response);
            dispatch("getManifest", params.manifestId);
          })
          .catch((error) => {
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
          });
      },

      releaseOutbound: ({ getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .releaseOutbound(params.manifestId, {
            door: params.door,
            trailer: params.trailer,
          })
          .then((response) => {
            console.log(response);
            return response;
          })
          .catch((error) => rejectHandled(dispatch, error));
      },
      synch: ({ getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .synch(params.manifestId)
          .then((response) => {
            console.log(response);
            return response;
          })
          .catch((error) => {
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
          });
      },
      forceUnload: ({ getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .forceUnload(params.manifestId)
          .then((response) => {
            console.log(response);
            return response;
          })
          .catch((error) => {
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
          });
      },
      load: ({ getters, dispatch }, params) => {
        const client = getters.appClient;
        params.item.wasManual = true;
        return client
          .manifestLoad(params.manifestId, params.item)
          .then((response) => {
            console.log(response);
            const cmd = response?.result;
            if (cmd && cmd.isSuccess === false) {
              const msg = cmd.failureReason || "Load failed";
              dispatch("toast/error", msg, { root: true });
              const err = new Error(msg);
              err.name = "CommandRejected";
              return Promise.reject(err);
            }
            dispatch("getManifest", params);
            return response;
          })
          .catch((error) => {
            if (error && error.name === "CommandRejected") {
              return Promise.reject(error);
            }
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
            return Promise.reject(error);
          });
      },
      unload: ({ getters, dispatch }, params) => {
        const client = getters.appClient;
        params.item.wasManual = true;
        return client
          .manifestUnload(params.manifestId, params.item)
          .then((response) => {
            console.log(response);
            const cmd = response?.result?.result;
            if (cmd && cmd.isSuccess === false) {
              const msg = cmd.failureReason || "Unload failed";
              dispatch("toast/error", msg, { root: true });
              const err = new Error(msg);
              err.name = "CommandRejected";
              return Promise.reject(err);
            }
            return response;
          })
          .catch((error) => {
            if (error && error.name === "CommandRejected") {
              return Promise.reject(error);
            }
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
            return Promise.reject(error);
          });
      },
      stow: ({ getters, dispatch }, params) => {
        const client = getters.appClient;
        params.item.wasManual = true;
        return client
          .manifestStow(params.manifestId, params.item)
          .then((response) => {
            console.log(response);
            const cmd = response?.result;
            if (cmd && cmd.isSuccess === false) {
              const msg = cmd.failureReason || "Stow failed";
              dispatch("toast/error", msg, { root: true });
              const err = new Error(msg);
              err.name = "CommandRejected";
              return Promise.reject(err);
            }
            dispatch("getManifest", params);
            return response;
          })
          .catch((error) => {
            if (error && error.name === "CommandRejected") {
              return Promise.reject(error);
            }
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
            return Promise.reject(error);
          });
      },
      pullItem: ({ getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .pullItem(params.id, params.barcode, params.location)
          .then((response) => {
            console.log(response);
            if (!response.isSuccess) throw new Error(response.failureReason);
          })
          .catch((error) => {
            console.log("ERROR!", error);
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            }
            if (response.isError) throw new Error(response.title);
          });
      },
      returnPro: ({ getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .returnPro(params.manifestId, params.proNumber, params)
          .then((response) => {
            console.log(response);
            if (!response.isSuccess) throw new Error(response.failureReason);
          })
          .catch((error) => {
            console.log("ERROR!", error);
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            }
            if (response.isError) throw new Error(response.title);
          });
      },
      updateCarrier: ({ getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .updateCarrier(params.manifestId, { carrier: params.carrier })
          .then((response) => {
            console.log(response);
            dispatch("getManifest", params);
          })
          .catch((error) => {
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
          });
      },
      updateTag: ({ getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .updateTag(params.manifestId, { tag: params.tag })
          .then((response) => {
            console.log(response);
            dispatch("getManifest", params);
          })
          .catch((error) => {
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
          });
      },
      updateXref: ({ getters, dispatch }, params) => {
        const client = getters.client;
        console.log("mod", params);
        return client
          .updateXref(params.manifestId, { xref: params.xref })
          .then((response) => {
            console.log(response);
            dispatch("getManifest", params);
          })
          .catch((error) => {
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
          });
      },
      reorderStops: ({ getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .reorderStops(params.manifestId, { manifest: params.manifestId, stopOrders: params.stops })
          .then((response) => {
            console.log(response);
            dispatch("getManifest", params);
          })
          .catch((error) => {
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
          });
      },
      assignProToLineHaulStop: ({ getters, dispatch }, params) => {
        const client = getters.client;
        return client
          .assignProsToLineHaulStop(
            params.manifestId,
            params.proNumbers,
            params.terminal
          )
          .then((response) => {
            console.log(response);
            dispatch("getManifest", params);
            return response;
          })
          .catch((error) => {
            let title = error.title || "Error";

            if (error.response) {
              const response = JSON.parse(error.response);
              title = response.title;
            }
            if (error.isError && error.status == 401) {
              dispatch("auth/logout", {}, { root: true });
            } else {
              dispatch("toast/error", title, { root: true });
            }
            return error;
          });
      },
    },
    mutations: {
      SET_ACTIVE_MANIFEST(state, data) {
        state._activeManifest = data;
      },
      SET_STATUSES(state, data) {
        state._statuses = data;
      },
      SET_MANIFESTS(state, data) {
        if (data == null || data.length == 0) return [];
        state._manifests = data;
      },
      SET_TOTAL_RECORDS(state, data) {
        state._totalRecords = data;
      },
      SET_PAGE(state, data) {
        state._page = data;
      },
    },
  };
};

export default (app) => createStore(app);
